import { UniversalWeightType } from '../database';

/**
 * Convert any universal weight type to 1RM equivalent
 */
export function toOneRepMax(weight: number, type: UniversalWeightType): number {
  switch (type) {
    case '1RM':
      return weight;
    case '5RM':
      return weight * 1.15; // 5RM is typically ~87% of 1RM
    case '5RM_1RIR':
      return weight * 1.12; // 5RM with 1 RIR is slightly less than true 5RM
    default:
      return weight;
  }
}

/**
 * Calculate target weight for specific rep count using Epley formula
 * Formula: 1RM / (1 + reps/30)
 */
export function calculateWeightForReps(oneRM: number, targetReps: number): number {
  const calculatedWeight = oneRM / (1 + targetReps / 30);
  // Round to nearest 0.25 lbs to match current system
  return Math.round(calculatedWeight * 4) / 4;
}

/**
 * Get suggested weight for an exercise based on universal weight and target reps
 */
export function getSuggestedWeight(
  universalWeight: number | undefined,
  universalWeightType: UniversalWeightType | undefined,
  targetReps: number
): number | null {
  if (!universalWeight || !universalWeightType) {
    return null;
  }

  const oneRM = toOneRepMax(universalWeight, universalWeightType);
  return calculateWeightForReps(oneRM, targetReps);
}

/**
 * Calculate what the universal weight should be based on current performance
 * Used for suggesting updates to universal weight
 */
export function calculateUniversalWeight(
  currentWeight: number,
  currentReps: number,
  targetType: UniversalWeightType
): number {
  // First convert current performance to 1RM
  const oneRM = currentWeight * (1 + currentReps / 30);
  
  // Then convert to target type
  switch (targetType) {
    case '1RM':
      return oneRM;
    case '5RM':
      return oneRM / 1.15;
    case '5RM_1RIR':
      return oneRM / 1.12;
    default:
      return oneRM;
  }
}

/**
 * Check if user's weight is significantly different from suggestion
 * Returns true if difference is > 10%
 */
export function isSignificantWeightDifference(
  suggestedWeight: number,
  actualWeight: number,
  threshold: number = 0.1
): boolean {
  const percentDifference = Math.abs(actualWeight - suggestedWeight) / suggestedWeight;
  return percentDifference > threshold;
}

/**
 * Format universal weight type for display
 */
export function formatUniversalWeightType(type: UniversalWeightType): string {
  switch (type) {
    case '1RM':
      return '1 Rep Max';
    case '5RM':
      return '5 Rep Max';
    case '5RM_1RIR':
      return '5 Reps @ 1 RIR';
    default:
      return type;
  }
}